import { supabase } from "../supabase";
import { getActiveOrganizationId, subscribeActiveOrg } from "../org";
import type {
  NewSubscriptionInput,
  Subscription,
  SubscriptionCadence,
  SubscriptionStatus,
  SubscriptionStore,
} from "./types";

interface SubscriptionRow {
  id: string;
  user_id: string;
  vendor: string;
  category_id: string | null;
  cadence: SubscriptionCadence | null;
  last_charge_cents: number | null;
  last_charge_on: string | null;
  status: SubscriptionStatus;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

interface CategoryLookupRow {
  id: string;
  label: string;
}

const ROW_COLUMNS =
  "id, user_id, vendor, category_id, cadence, last_charge_cents, last_charge_on, status, notes, created_at, updated_at";

function rowToSubscription(
  row: SubscriptionRow,
  categoryLabel: string | null,
): Subscription {
  return {
    id: row.id,
    vendor: row.vendor,
    categoryId: row.category_id,
    categoryLabel,
    cadence: row.cadence,
    lastChargeCents:
      row.last_charge_cents === null ? null : Number(row.last_charge_cents),
    lastChargeOn: row.last_charge_on,
    status: row.status,
    notes: row.notes,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function createSupabaseSubscriptionStore(): SubscriptionStore {
  const listeners = new Set<() => void>();

  let snapshot: Subscription[] = [];
  const categoryLabels = new Map<string, string>();

  let initialized = false;
  let loadPromise: Promise<void> | null = null;

  function emit(): void {
    for (const listener of listeners) {
      listener();
    }
  }

  function commit(next: Subscription[]): void {
    snapshot = next;
    emit();
  }

  async function loadLookups(orgId: string): Promise<void> {
    const { data, error } = await supabase
      .from("categories")
      .select("id, label")
      .eq("organization_id", orgId);
    categoryLabels.clear();
    if (!error && data) {
      for (const row of data as CategoryLookupRow[]) {
        categoryLabels.set(row.id, row.label);
      }
    }
  }

  async function loadFromServer(): Promise<void> {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData?.user) {
      categoryLabels.clear();
      commit([]);
      return;
    }

    const orgId = getActiveOrganizationId();
    if (!orgId) {
      categoryLabels.clear();
      commit([]);
      return;
    }

    await loadLookups(orgId);

    const { data: rows, error } = await supabase
      .from("subscriptions")
      .select(ROW_COLUMNS)
      .eq("organization_id", orgId)
      .order("created_at", { ascending: false });

    if (error) {
      // eslint-disable-next-line no-console
      console.warn(`[subscriptions] list failed: ${error.message}`);
      commit([]);
      return;
    }

    const typedRows = (rows ?? []) as SubscriptionRow[];
    const next = typedRows.map((row) =>
      rowToSubscription(
        row,
        row.category_id ? (categoryLabels.get(row.category_id) ?? null) : null,
      ),
    );
    commit(next);
  }

  function ensureLoaded(): Promise<void> {
    if (initialized) return Promise.resolve();
    if (!loadPromise) {
      loadPromise = loadFromServer().finally(() => {
        initialized = true;
        loadPromise = null;
      });
    }
    return loadPromise;
  }

  let authSubAttached = false;
  function attachAuthListener(): void {
    if (authSubAttached) return;
    authSubAttached = true;
    supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        initialized = false;
        categoryLabels.clear();
        commit([]);
        return;
      }
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        initialized = false;
        void ensureLoaded();
      }
    });
    subscribeActiveOrg(() => {
      initialized = false;
      categoryLabels.clear();
      void ensureLoaded();
    });
  }

  return {
    list(): Subscription[] {
      if (!initialized && !loadPromise) {
        void ensureLoaded();
      }
      return snapshot;
    },

    get(id: string): Subscription | undefined {
      return snapshot.find((s) => s.id === id);
    },

    async add(input: NewSubscriptionInput): Promise<Subscription> {
      const { data: userData, error: userError } =
        await supabase.auth.getUser();
      if (userError || !userData?.user) {
        throw new Error("You must be signed in to create a subscription.");
      }
      const userId = userData.user.id;

      const orgId = getActiveOrganizationId();
      if (!orgId) {
        throw new Error(
          "No active organization selected. Reload the page and try again.",
        );
      }

      const subId = crypto.randomUUID();

      const { data: inserted, error: insertError } = await supabase
        .from("subscriptions")
        .insert({
          id: subId,
          organization_id: orgId,
          user_id: userId,
          vendor: input.vendor,
          category_id: input.categoryId ?? null,
          cadence: input.cadence ?? null,
          last_charge_cents: input.lastChargeCents ?? null,
          last_charge_on: input.lastChargeOn ?? null,
          status: input.status ?? "active",
          notes: input.notes ?? null,
        })
        .select(ROW_COLUMNS)
        .single();

      if (insertError || !inserted) {
        throw new Error(
          `Failed to create subscription: ${insertError?.message ?? "unknown error"}`,
        );
      }

      if (input.categoryId && !categoryLabels.has(input.categoryId)) {
        if (input.categoryLabel) {
          categoryLabels.set(input.categoryId, input.categoryLabel);
        }
      }

      const row = inserted as SubscriptionRow;
      const categoryLabel = row.category_id
        ? (categoryLabels.get(row.category_id) ?? null)
        : null;
      const sub = rowToSubscription(row, categoryLabel);

      initialized = true;
      commit([sub, ...snapshot]);
      return sub;
    },

    async update(
      id: string,
      patch: Partial<Omit<Subscription, "id" | "createdAt" | "updatedAt">>,
    ): Promise<Subscription> {
      const dbPatch: Record<string, unknown> = {};
      if (patch.vendor !== undefined) dbPatch.vendor = patch.vendor;
      if (patch.categoryId !== undefined)
        dbPatch.category_id = patch.categoryId;
      if (patch.cadence !== undefined) dbPatch.cadence = patch.cadence;
      if (patch.lastChargeCents !== undefined)
        dbPatch.last_charge_cents = patch.lastChargeCents;
      if (patch.lastChargeOn !== undefined)
        dbPatch.last_charge_on = patch.lastChargeOn;
      if (patch.status !== undefined) dbPatch.status = patch.status;
      if (patch.notes !== undefined) dbPatch.notes = patch.notes;

      const { data, error } = await supabase
        .from("subscriptions")
        .update(dbPatch)
        .eq("id", id)
        .select(ROW_COLUMNS)
        .single();

      if (error || !data) {
        throw new Error(
          `Failed to update subscription: ${error?.message ?? "unknown"}`,
        );
      }

      const row = data as SubscriptionRow;
      if (patch.categoryLabel && row.category_id) {
        categoryLabels.set(row.category_id, patch.categoryLabel);
      }
      const categoryLabel = row.category_id
        ? (categoryLabels.get(row.category_id) ?? null)
        : null;
      const updated = rowToSubscription(row, categoryLabel);

      const idx = snapshot.findIndex((s) => s.id === id);
      if (idx !== -1) {
        const next = snapshot.slice();
        next[idx] = updated;
        commit(next);
      }
      return updated;
    },

    async remove(id: string): Promise<void> {
      const target = snapshot.find((s) => s.id === id);
      if (!target) return;
      const next = snapshot.filter((s) => s.id !== id);
      commit(next);
      void supabase
        .from("subscriptions")
        .delete()
        .eq("id", id)
        .then(({ error }) => {
          if (error) {
            // eslint-disable-next-line no-console
            console.warn(`[subscriptions] delete failed: ${error.message}`);
          }
        });
    },

    subscribe(listener: () => void): () => void {
      attachAuthListener();
      if (!initialized && !loadPromise) {
        void ensureLoaded();
      }
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
